import React from 'react';
import type { UserProfile } from '../../types';
import { RoddoFishIcon } from '../icons';

interface Props {
  userProfile: UserProfile;
  isDarkMode: boolean;
  isSaving?: boolean;
  onStart: (displayName: string) => void | Promise<void>;
}

const clean = (value: unknown) => String(value ?? '').trim();

export default function RoddoWelcomeCard({
  userProfile,
  isDarkMode,
  isSaving = false,
  onStart,
}: Props) {
  const initialName =
    clean((userProfile as any)?.displayName) ||
    clean((userProfile as any)?.username) ||
    clean(userProfile?.name) ||
    'Angler';
  const [displayName, setDisplayName] = React.useState(initialName);

  React.useEffect(() => {
    setDisplayName(initialName);
  }, [initialName]);

  const bg = isDarkMode ? '#121A2B' : '#FFFCF7';
  const raised = isDarkMode ? '#182238' : '#F2EBE1';
  const text = isDarkMode ? '#F4F6FA' : '#30271E';
  const muted = isDarkMode ? '#AEB8C9' : '#6E6255';
  const border = isDarkMode ? 'rgba(180,195,220,.18)' : 'rgba(74,58,42,.16)';
  const accent = isDarkMode ? '#7057E8' : '#2F80ED';

  return (
    <div className="pointer-events-none absolute inset-0 z-[1180] flex items-center justify-center px-4 pb-[calc(var(--roddo-nav-h,76px)+20px)] pt-[calc(var(--roddo-header-h,72px)+20px)]">
      <div
        className="pointer-events-auto w-full max-w-[380px] rounded-[20px] border p-5"
        style={{
          background: bg,
          borderColor: border,
          color: text,
          boxShadow: isDarkMode
            ? '0 22px 60px rgba(0,0,0,.42)'
            : '0 22px 54px rgba(67,49,30,.18)',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px] border"
            style={{ background: raised, borderColor: border }}
          >
            <RoddoFishIcon className="h-8 w-8" mode="png" animated={false} />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: accent }}>
              First cast
            </div>
            <h1 className="mt-0.5 text-[24px] font-black tracking-[-0.035em]">Welcome to Roddo</h1>
          </div>
        </div>

        <p className="mt-4 text-sm font-semibold leading-6" style={{ color: muted }}>
          Explore the water. Log what happens. Build your fishing story.
        </p>

        <label className="mt-5 block text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: muted }}>
          Angler name
        </label>
        <input
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          maxLength={40}
          autoComplete="nickname"
          className="mt-2 w-full rounded-[13px] border px-4 py-3 text-[15px] font-bold outline-none"
          style={{ background: raised, borderColor: border, color: text }}
        />

        <button
          type="button"
          disabled={isSaving || !clean(displayName)}
          onClick={() => void onStart(clean(displayName) || initialName)}
          className="mt-4 w-full rounded-[13px] px-4 py-3.5 text-sm font-black text-white transition active:scale-[.985] disabled:opacity-55"
          style={{ background: accent }}
        >
          {isSaving ? 'Getting Roddo ready…' : 'Start Exploring'}
        </button>

        <div className="mt-3 text-center text-[11px] font-semibold" style={{ color: muted }}>
          Continue as guest. You can link an account later in Profile.
        </div>
      </div>
    </div>
  );
}
